import { storableError } from '../../util/errors';
import * as allyApi from '../../util/allyApi';

// ================ Action types ================ //

export const START_SESSION_REQUEST = 'app/AllyPage/START_SESSION_REQUEST';
export const START_SESSION_SUCCESS = 'app/AllyPage/START_SESSION_SUCCESS';
export const START_SESSION_ERROR = 'app/AllyPage/START_SESSION_ERROR';

export const FETCH_SESSIONS_REQUEST = 'app/AllyPage/FETCH_SESSIONS_REQUEST';
export const FETCH_SESSIONS_SUCCESS = 'app/AllyPage/FETCH_SESSIONS_SUCCESS';
export const FETCH_SESSIONS_ERROR = 'app/AllyPage/FETCH_SESSIONS_ERROR';

export const SET_ACTIVE_SESSION = 'app/AllyPage/SET_ACTIVE_SESSION';
export const UPDATE_SESSION_TITLE = 'app/AllyPage/UPDATE_SESSION_TITLE';

// ================ Reducer ================ //

const initialState = {
  sessions: [],
  activeSessionId: null,
  isLoading: false,
  error: null,
};

export default function allyPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    // Start session
    case START_SESSION_REQUEST:
      return { ...state, isLoading: true, error: null };
    case START_SESSION_SUCCESS:
      return {
        ...state,
        isLoading: false,
        activeSessionId: payload.sessionId,
        sessions: payload.resuming
          ? state.sessions
          : [
              {
                sessionId: payload.sessionId,
                createdAt: new Date().toISOString(),
                title: null,
              },
              ...state.sessions,
            ],
      };
    case START_SESSION_ERROR:
      return { ...state, isLoading: false, error: payload };

    // Fetch sessions
    case FETCH_SESSIONS_REQUEST:
      return { ...state, isLoading: true, error: null };
    case FETCH_SESSIONS_SUCCESS:
      return { ...state, isLoading: false, sessions: payload.sessions || [] };
    case FETCH_SESSIONS_ERROR:
      return { ...state, isLoading: false, error: payload };

    // Set active session
    case SET_ACTIVE_SESSION:
      return { ...state, activeSessionId: payload };

    // Update session title in sidebar
    case UPDATE_SESSION_TITLE:
      return {
        ...state,
        sessions: state.sessions.map(s =>
          s.sessionId === payload.sessionId ? { ...s, title: payload.title } : s
        ),
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const startSessionRequest = () => ({ type: START_SESSION_REQUEST });
const startSessionSuccess = data => ({ type: START_SESSION_SUCCESS, payload: data });
const startSessionError = error => ({ type: START_SESSION_ERROR, payload: error });

const fetchSessionsRequest = () => ({ type: FETCH_SESSIONS_REQUEST });
const fetchSessionsSuccess = data => ({ type: FETCH_SESSIONS_SUCCESS, payload: data });
const fetchSessionsError = error => ({ type: FETCH_SESSIONS_ERROR, payload: error });

export const updateSessionTitle = (sessionId, title) => ({
  type: UPDATE_SESSION_TITLE,
  payload: { sessionId, title },
});

// ================ Thunks ================ //

export const setActiveSession = sessionId => dispatch => {
  dispatch({ type: SET_ACTIVE_SESSION, payload: sessionId });
};

export const startSession = (listingTypeId = null) => async dispatch => {
  dispatch(startSessionRequest());

  try {
    const response = await allyApi.startSession(listingTypeId);
    dispatch(startSessionSuccess(response));
    return response;
  } catch (e) {
    const error = storableError(e);
    dispatch(startSessionError(error.message || 'Failed to start session'));
    throw e;
  }
};

export const fetchSessions = (limit = 10) => async dispatch => {
  dispatch(fetchSessionsRequest());

  try {
    const response = await allyApi.getSessions(limit);
    dispatch(fetchSessionsSuccess(response));
    return response;
  } catch (e) {
    const error = storableError(e);
    dispatch(fetchSessionsError(error.message || 'Failed to fetch sessions'));
    throw e;
  }
};

// Load data for SSR (server-side rendering)
export const loadData = () => async dispatch => {
  // AllyPage doesn't need SSR data loading
  // Sessions are loaded client-side after authentication
  return Promise.resolve();
};
